/**
 * Locomotion tuning (docs/02 §2, §4, §6). Units: meters, seconds, degrees; ticks are 60 Hz.
 * These are v0 feel targets. The Training-mode tuning panel edits them live and exports them
 * back here with "copy as TS".
 */
export interface MovementTuning {
  /** Collision capsule: radius and cylinder half-height. The capsule's bottom sits at the feet. */
  capsule: { radius: number; halfHeight: number };
  walkSpeed: number;
  runSpeed: number;
  sprintSpeed: number;
  /** Ground acceleration and braking, m/s². */
  accel: number;
  decel: number;
  /** Planar acceleration while airborne, m/s². */
  airAccel: number;
  /** Facing turn rate, deg/s, and the extra rate when turning more than 120°. */
  turnRate: number;
  turnRateSharp: number;
  jumpVelocity: number;
  gravity: number;
  /** Gravity multiplier while falling, for a snappier arc. */
  fallGravityScale: number;
  maxFallSpeed: number;
  coyoteTicks: number;
  /** Press actions (jump, dodge) wait this long for a legal moment (150 ms). */
  bufferTicks: number;
  dodge: {
    speed: number;
    ticks: number;
    iframeTicks: number;
    recoveryTicks: number;
    stamina: number;
    /** Speed multiplier during recovery ticks. */
    recoverySpeed: number;
  };
  stamina: {
    max: number;
    regenPerSec: number;
    regenDelayTicks: number;
    sprintDrainPerSec: number;
    /** Sprint needs at least this much to start again after running dry. */
    sprintRestart: number;
  };
  brace: { speedMul: number };
}

export const DEFAULT_MOVEMENT: MovementTuning = {
  capsule: { radius: 0.45, halfHeight: 0.4 },
  walkSpeed: 2.2,
  runSpeed: 6.2,
  sprintSpeed: 9.0,
  accel: 38,
  decel: 30,
  airAccel: 12,
  turnRate: 540,
  turnRateSharp: 900,
  jumpVelocity: 7.2,
  gravity: 22,
  fallGravityScale: 1.45,
  maxFallSpeed: 30,
  coyoteTicks: 6,
  bufferTicks: 9,
  dodge: {
    speed: 13,
    ticks: 16,
    iframeTicks: 12,
    recoveryTicks: 8,
    stamina: 25,
    recoverySpeed: 0.35,
  },
  stamina: {
    max: 100,
    regenPerSec: 45,
    regenDelayTicks: 36,
    sprintDrainPerSec: 18,
    sprintRestart: 20,
  },
  brace: { speedMul: 0.35 },
};

/**
 * Per-creature overrides. AGI scales speeds (docs/02): 100 AGI is the baseline.
 * Capsules match the exported models (Kilnback 2.3 m long, 1.2 m tall; Rillwing 2.2 m tall).
 */
export const CREATURE_MOVEMENT: Record<string, Partial<MovementTuning>> = {
  kilnback: { capsule: { radius: 0.6, halfHeight: 0.05 } },
  rillwing: { capsule: { radius: 0.38, halfHeight: 0.62 } },
};

export function movementFor(creatureId: string, agi = 100): MovementTuning {
  const base = { ...DEFAULT_MOVEMENT, ...CREATURE_MOVEMENT[creatureId] };
  const k = 0.75 + 0.25 * (agi / 100);
  return {
    ...base,
    walkSpeed: base.walkSpeed * k,
    runSpeed: base.runSpeed * k,
    sprintSpeed: base.sprintSpeed * k,
    dodge: { ...base.dodge, speed: base.dodge.speed * (0.85 + 0.15 * (agi / 100)) },
  };
}
